import { useState } from "react";
import {
  Button,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Delete, FileOpen, Visibility, VisibilityOff } from "@mui/icons-material";
import { formatDateString } from "../../common/FormatDateString";
import {
  useDeleteComplianceTrainingRecordMutation,
  useDownloadComplianceTrainingRecordMutation,
  useGetProfileComplianceRecordsQuery,
} from "../../redux/api/complianceTraining";
import { ComplianceTrainingRecord } from "../../common/ComplianceTraining";
import ComplianceTrainingRecordUpdateModal from "./ComplianceTrainingRecordUpdateModal";
import ComplianceTrainingRecordUploadModal from "./ComplianceTrainingRecordUploadModal";

interface ProfileComplianceTrainingDocumentsProps {
  profileId: number;
  isViewOnly: boolean;
}

const isExpired = (record: ComplianceTrainingRecord): boolean => {
  if (!record.expirationDate) return true;
  return new Date(record.expirationDate) < new Date();
};

const ProfileComplianceTrainingDocuments = ({ profileId, isViewOnly }: ProfileComplianceTrainingDocumentsProps) => {
  const [showExpired, setShowExpired] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currRecordId, setCurrRecordId] = useState<number>();

  const { data: records = [], refetch } = useGetProfileComplianceRecordsQuery(profileId, {
    refetchOnMountOrArgChange: true,
  });
  const [downloadComplianceTrainingRecord] = useDownloadComplianceTrainingRecordMutation();
  const [deleteComplianceTrainingRecord] = useDeleteComplianceTrainingRecordMutation();

  const displayed = showExpired ? records : records.filter((r) => !isExpired(r));

  const handleView = async (recordId: number, contentType: string) => {
    const response = await downloadComplianceTrainingRecord(recordId);
    if ("error" in response) return;
    const url = window.URL.createObjectURL(response.data as Blob);
    const inlineTypes = ["application/pdf", "image/png", "image/jpeg"];
    if (inlineTypes.includes(contentType)) {
      window.open(url, "_blank");
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.download = `compliance-training-document`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenDelete = (recordId: number) => {
    setCurrRecordId(recordId);
    setConfirmDelete(true);
  };

  const handleCloseDelete = () => {
    setConfirmDelete(false);
  };

  const deleteRecord = async () => {
    if (currRecordId === undefined) return;
    await deleteComplianceTrainingRecord(currRecordId);
    refetch();
    handleCloseDelete();
  };

  return (
    <>
      <TableContainer>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <h3 className="mb-0">Compliance Training</h3>
          </div>
          <div>
            <Button
              className="grey-link-button"
              onClick={() => setShowExpired(!showExpired)}
            >
              {showExpired ? <Visibility /> : <VisibilityOff />}
              Show Expired Documents
            </Button>
          </div>
        </div>
        <Table className="documents-table">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: "35%" }}>TRAINING</TableCell>
              <TableCell>ISSUE DATE</TableCell>
              <TableCell>EXPIRATION DATE</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {displayed.map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record.trainingName}</TableCell>
                <TableCell>{record.issueDate ? formatDateString(record.issueDate) : ""}</TableCell>
                <TableCell>
                  {record.expirationDate ? formatDateString(record.expirationDate) : ""}
                </TableCell>
                <TableCell className="document-actions">
                  {record.fileUrl && record.contentType && (
                    <Button
                      className="blue-link-button"
                      onClick={() => handleView(record.id, record.contentType!)}
                    >
                      <FileOpen />
                      View
                    </Button>
                  )}
                  {!isViewOnly && (
                    <>
                      <ComplianceTrainingRecordUpdateModal record={record} onUpdate={refetch} />
                      <Button
                        className="blue-link-button"
                        onClick={() => handleOpenDelete(record.id)}
                      >
                        <Delete />
                        Delete
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!isViewOnly && (
        <ComplianceTrainingRecordUploadModal profileId={profileId} onUpload={refetch} />
      )}

      <Dialog
        open={confirmDelete}
        onClose={handleCloseDelete}
        aria-labelledby="modal-title"
      >
        <Typography variant="h3" id="modal-title">
          Delete Record
        </Typography>
        <p>Are you sure you would like to delete this record?</p>
        <div className="modal-footer">
          <Button className="outline-button-small" onClick={deleteRecord}>
            Confirm
          </Button>
          <Button className="blue-button-small" onClick={handleCloseDelete}>
            Cancel
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default ProfileComplianceTrainingDocuments;
